import { useState } from 'react';
import { useSnackbars } from '../../../../hooks/snackbar.hook';
import { AxiosError } from 'axios';
import { showAxiosMessageError } from '../../../../utils/error-message.util';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';
import Button from '@mui/material/Button';
import { ProposalAssignmentDto } from '../../../../services/dtos/assignments.dto';
import { sendConfirmationProposalAssignment } from '../../../../services/assignments.service';
import './proposal-assignment-dialog.form.scss';

/**
 * Dialog de formulaire
 *
 * @param {boolean} props.open - Etat du dialog
 * @param {function} props.handleClose - Fonction de fermeture du dialog
 * @param {Object} props.userInfos - Informations de l'utilsateur
 */
export default function ProposalAssignmentDialogForm(
    props: Readonly<{
        open: boolean;
        proposalAssignment?: ProposalAssignmentDto;
        mandateId: string;
        handleClose: () => void;
        handleConfirmationProposalAssignment: () => void;
        confirmation?: boolean;
    }>
) {
    // Show message
    const showMessage = useSnackbars();
    // en cours de validation, on bloque le bouton
    const [validate, setValidate] = useState(false);
    const [motifRefus, setMotifRefus] = useState('');
    const [motifRefusError, setMotifRefusError] = useState(false);

    /**
     * Fonction d'envoi du formulaire
     *
     * @param proposalAssignment
     * @param mandateId
     * @param confirmation
     */
    const handleSubmit = (mandateId: string, proposalAssignment?: ProposalAssignmentDto, confirmation?: boolean) => {
        if (proposalAssignment && confirmation !== undefined) {
            const motif = motifRefus.trim();
            if (!confirmation && motif === '') {
                setMotifRefusError(true);
                return;
            }

            setValidate(true);
            sendConfirmationProposalAssignment(proposalAssignment, mandateId, confirmation, motif)
                .then(() => {
                    setMotifRefus('');
                    setMotifRefusError(false);
                    showMessage.successAlert(
                        confirmation
                            ? 'La proposition a été envoyée en signature électronique.'
                            : 'Le refus a été enregistré.'
                    );
                    props.handleConfirmationProposalAssignment();
                    props.handleClose();
                })
                .catch((error: Error | AxiosError) => {
                    showAxiosMessageError(error, showMessage);
                })
                .finally(() => {
                    setValidate(false);
                });
        }
    };

    return (
        <Dialog
            open={props.open}
            onClose={props.handleClose}
            className={'proposal-confirmation-dialog'}
            sx={{ padding: '0px' }}
        >
            <DialogTitle sx={{ borderRadius: '0px' }} variant={'h5'}>
                {props.confirmation ? 'Valider la proposition' : 'Refuser la proposition'}{' '}
                {props.proposalAssignment?.reference}
            </DialogTitle>
            <DialogContent>
                {/* Le texte sera adapté selon s'il s'agit d'un refus ou d'une validation*/}
                <DialogContentText
                    variant={'body1'}
                    style={{
                        marginBottom: '10px',
                        fontWeight: 'bold',
                        color: 'black',
                        textAlign: 'center',
                    }}
                >
                    Êtes-vous sûr de vouloir {props.confirmation ? 'valider' : 'refuser'} la proposition d'affectations
                    ?
                </DialogContentText>
                {!props.confirmation && (
                    <TextField
                        label="Motif du refus"
                        value={motifRefus}
                        onChange={event => {
                            setMotifRefus(event.target.value);
                            setMotifRefusError(false);
                        }}
                        error={motifRefusError}
                        helperText={motifRefusError ? 'Le motif du refus est obligatoire' : ''}
                        multiline
                        minRows={4}
                        fullWidth
                        required
                        data-testid="motif-refus"
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Typography variant={'button'}>
                    <Button variant="contained" onClick={props.handleClose} data-testid={'close'}>
                        Retour
                    </Button>
                </Typography>
                <Typography variant={'button'}>
                    <Button
                        type="submit"
                        variant="contained"
                        onClick={() => handleSubmit(props.mandateId, props.proposalAssignment, props.confirmation)}
                        disabled={validate}
                    >
                        Confirmer
                    </Button>
                </Typography>
            </DialogActions>
        </Dialog>
    );
}
